import React, { useRef } from 'react';
import { DeleteIcon } from './icons/DeleteIcon';
import { UploadIcon } from './icons/UploadIcon';
import { RefreshIcon } from './icons/RefreshIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';

const formatDate = (timestamp) => {
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(timestamp));
};

export const ProjectDashboard = ({ projects, onCreate, onLoad, onDelete, onImport, onRefresh, isLoading }) => {
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            onImport(file);
        }
        if (e.target) e.target.value = '';
    };

    return (
        <div className="w-full h-full overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div className="flex items-center gap-4">
                        <h1 className="text-3xl font-bold">My Projects</h1>
                        <button 
                            onClick={onRefresh} 
                            disabled={isLoading}
                            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                            title="Refresh project list from backend"
                        >
                            <RefreshIcon className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                    <div className="flex gap-4 flex-wrap">
                        <input 
                            type="file" 
                            accept=".json" 
                            ref={fileInputRef} 
                            onChange={handleFileChange} 
                            className="hidden" 
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm md:text-base flex items-center"
                        >
                            <UploadIcon className="w-5 h-5 mr-2" />
                            Import
                        </button>
                        <button
                            onClick={onCreate}
                            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm md:text-base"
                        >
                            + Create New Project
                        </button>
                    </div>
                </div>

                <div className="bg-gray-900/50 border border-gray-800 rounded-lg min-h-[300px]">
                    {isLoading && projects.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64">
                            <LoadingSpinner className="w-8 h-8 mb-2 text-purple-500" />
                            <p className="text-gray-400">Loading projects...</p>
                        </div>
                    ) : projects.length > 0 ? (
                        <ul className="divide-y divide-gray-800">
                            {projects.map(project => (
                                <li key={project.id} className="p-4 flex justify-between items-center group hover:bg-gray-800/30 transition-colors">
                                    <div>
                                        <h2 className="text-lg md:text-xl font-semibold text-gray-200">{project.name}</h2>
                                        <p className="text-sm text-gray-500">Last updated: {formatDate(project.lastModified)}</p>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <button
                                            onClick={() => onLoad(project.id)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1.5 px-4 rounded-md transition-colors text-sm"
                                        >
                                            Open
                                        </button>
                                        <button
                                            onClick={() => onDelete(project.id)}
                                            className="p-2 rounded-md hover:bg-red-500/20 text-red-400 transition-colors"
                                            aria-label={`Delete ${project.name}`}
                                        >
                                            <DeleteIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-16 px-8 flex flex-col items-center">
                            <h2 className="text-2xl font-semibold text-gray-400">Welcome to LoopAi-adstudio!</h2>
                            <p className="text-gray-500 mt-2 mb-6">You don't have any projects yet.</p>
                            <div className="flex gap-4">
                                <button 
                                    onClick={onCreate}
                                    className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                                >
                                    Create Your First Project
                                </button>
                                <button 
                                    onClick={onRefresh}
                                    className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                                >
                                    Check for Cloud Projects
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
